import { minUtrValue, maxUtrValue } from '../../../utils/constants';

// ============================================================================
// FILTER KEYS
// ============================================================================
// All filter field names used in state and API requests

export const FILTER_KEYS = {
    // Core search
    TYPE: 'type',
    QUERY: 'query',

    // Location
    LOCATION: 'location',
    LOCATION_INPUT_VALUE: 'locationInputValue',
    LOCATION_COUNTRY_NAME: 'locationCountryName',
    LOCATION_STATE_NAME: 'locationStateName',
    LAT: 'lat',
    LNG: 'lng',
    DISTANCE: 'distance',
    PIN: 'pin',

    // Date
    DATE_TYPE: 'dateType',
    DATE_RANGE: 'dateRange',
    START_DATE: 'startDate',
    END_DATE: 'endDate',

    // Event properties
    EVENT_STATE: 'eventState',
    EVENT_TYPES: 'eventTypes',
    EVENT_UTR_TYPES: 'eventUtrTypes',
    EVENT_TEAM_TYPES: 'eventTeamTypes',

    // Sport & format
    SPORT_TYPES: 'sportTypes',
    BALL_TYPES: 'ballTypes',
    GENDERS: 'genders',

    // UTR
    UTR_TYPE: 'utrType',
    UTR_FORMAT: 'utrFormat',
    UTR_TEAM_TYPE: 'utrTeamType',
    UTR_MIN: 'utrMin',
    UTR_MAX: 'utrMax',
    UTR_FIT_POSITION: 'utrFitPosition',

    // Prize money
    PRIZE_MONEY_MIN: 'prizeMoneyMin',
    PRIZE_MONEY_MAX: 'prizeMoneyMax',

    // Tours
    TOURS: 'tours',
};

// ============================================================================
// FILTER VALUES
// ============================================================================
// Data values used for filters (not for display)

export const DATE_VALUES = {
    CUSTOM: 'custom',
    PAST: 'past',
    UPCOMING: 'upcoming',
    IN_PROGRESS: 'in_progress',
};

export const GENDER_VALUES = {
    MALE: 'm',
    FEMALE: 'f',
};

export const TEAM_TYPE_VALUES = {
    SINGLES: 'singles',
    DOUBLES: 'doubles',
    DINGLES: 'dingles',
};

export const SPORT_TYPE_VALUES = {
    TENNIS: 'tennis',
    PICKLEBALL: 'pickleball',
};

export const UTR_TYPE_VALUES = {
    MY_UTR: 'myutr',
    SANCTIONED: 'sanctioned',
    PLAYERS: 'players',
    VERIFIED: 'verified',
};

export const EVENT_STATE_VALUES = {
    OPEN_REGISTRATION: 'open_registration',
};

export const SEARCH_TYPE_VALUES = {
    EVENTS: 'events',
};

// Consolidated filter values for convenience
export const FILTER_VALUES = {
    ...DATE_VALUES,
    ...GENDER_VALUES,
    ...TEAM_TYPE_VALUES,
    ...SPORT_TYPE_VALUES,
    ...UTR_TYPE_VALUES,
    ...EVENT_STATE_VALUES,
    ...SEARCH_TYPE_VALUES,
    DEFAULT_DISTANCE: '200mi',
};

// ============================================================================
// UI TEXT & LABELS
// ============================================================================
// Display strings for UI components

export const UI_TEXT = {
    SECTION_TITLES: {
        DATE: 'Date',
        EVENT_TYPE: 'Event Type',
        BALL_TYPE: 'Ball Type',
        VERIFIED: 'Verified',
        FORMAT: 'Format',
        GENDER: 'Gender',
    },

    BUTTONS: {
        APPLY: 'Apply',
        CANCEL: 'Cancel',
        CLEAR: 'Clear',
        CLOSE: 'Close',
        MORE_FILTERS: 'More Filters',
    },

    DATE_LABELS: {
        CUSTOM: 'Custom',
        PAST: 'Past',
        START_DATE: 'From',
        END_DATE: 'To',
    },

    FORMAT_LABELS: {
        SINGLES: 'Singles',
        DOUBLES: 'Doubles',
        DINGLES: 'Dingles',
    },

    GENDER_LABELS: {
        MALE: 'Male',
        FEMALE: 'Female',
    },

    SPORT_TYPE_LABELS: {
        TENNIS: 'Tennis',
        PICKLEBALL: 'Pickleball',
        SPORT: 'Sport',
    },

    VERIFIED_LABELS: {
        UTR_EVENT: 'UTR Event',
        VERIFIED_UTR_EVENT: 'Verified UTR Event',
    },
};

// ============================================================================
// FILTER CONFIGURATION
// ============================================================================
// Metadata and configuration for filter behavior

export const PRIZE_MONEY_OPTIONS = [
    { id: 1, label: '$0' },
    { id: 2, label: '$500' },
    { id: 3, label: '$1K' },
    { id: 4, label: '$2.5K' },
    { id: 5, label: '$5K' },
    { id: 6, label: '$10K' },
    { id: 7, label: '$25K' },
];

export const FILTER_CONFIG = {
    // Filter keys that only affect UI state (not sent to API)
    UI_ONLY_KEYS: ['dateType', 'utrFormat', 'sportTypes'],

    // Filter keys that use checkbox inputs
    CHECKBOX_KEYS: ['eventTypes', 'ballTypes', 'eventUtrTypes', 'eventTeamTypes', 'genders'],

    // Filter keys that are allowed to be sent to the API
    ALLOWED_API_KEYS: [
        'utrMin',
        'utrMax',
        'utrTeamType',
        'prizeMoneyMin',
        'prizeMoneyMax',
        'eventTeamTypes',
        'genders',
        'eventTypes',
        'ballTypes',
        'eventUtrTypes',
        'startDate',
        'endDate',
        'dateRange',
        'sportTypes',
    ],
};

// ============================================================================
// QUERY-BASED FILTER BEHAVIOR
// ============================================================================
// Special handling rules for query-based searches

export const QUERY_BEHAVIOR = {
    // Fields cleared when there's a query (e.g., name search)
    FIELDS_CLEARED_BY_QUERY: [
        'eventState',
        'utrTeamType',
        'location',
        'lat',
        'lng',
        'distance',
        'locationCountryName',
        'locationStateName',
    ],

    // Fields excluded from URL when there's a query
    FIELDS_EXCLUDED_FROM_URL: [
        'eventState',
        'utrTeamType',
        'location',
        'lat',
        'lng',
        'distance',
        'locationCountryName',
        'locationStateName',
        'utrFitPosition',
        'startDate',
    ],

    // Fields that should respect explicit null values in initial filters
    FIELDS_RESPECTING_NULL: [
        'eventState',
        'utrTeamType',
        'location',
        'lat',
        'lng',
        'distance',
        'locationCountryName',
        'locationStateName',
    ],
};

// ============================================================================
// INTERNAL PROCESSING KEYS
// ============================================================================
// Keys used internally for complex filter processing

export const INTERNAL_KEYS = {
    UTR_RANGE: 'UTR_RANGE',
    PRIZE_MONEY_RANGE: 'PRIZE_MONEY_RANGE',
};

// ============================================================================
// DEFAULT VALUES
// ============================================================================
// Default filter state for event search

export const DEFAULT_FILTERS = {
    type: SEARCH_TYPE_VALUES.EVENTS,
    eventState: EVENT_STATE_VALUES.OPEN_REGISTRATION,
    utrMin: minUtrValue,
    utrMax: maxUtrValue,
    utrType: UTR_TYPE_VALUES.VERIFIED,
    utrTeamType: TEAM_TYPE_VALUES.SINGLES,
    sportTypes: {
        [SPORT_TYPE_VALUES.TENNIS]: true,
        [SPORT_TYPE_VALUES.PICKLEBALL]: false,
    },
    tours: {},
    eventTypes: {},
    eventUtrTypes: {},
    eventTeamTypes: {},
    genders: {},
    ballTypes: {},
    location: null,
    lat: null,
    lng: null,
    distance: FILTER_VALUES.DEFAULT_DISTANCE,
    locationCountryName: null,
    locationStateName: null,
};

// ============================================================================
// LEGACY ALIASES (for gradual migration)
// ============================================================================

export const FilterKeys = FILTER_KEYS;
export const FilterValues = FILTER_VALUES;

// ============================================================================
// URL PARAMETERS
// ============================================================================

export const UrlParams = {
    EVENT_STATE: 'eventState',
    PIN: 'pin',
    QUERY: 'query',
    RANGE: 'range',
    SEARCH_ORIGIN: 'searchOrigin',
    SHOW_PICKLEBALL_CONTENT: 'showPickleballContent',
    SHOW_TENNIS_CONTENT: 'showTennisContent',
    SKIP: 'skip',
    SORT: 'sort',
    SORT_BY: 'sortBy',
    TOP: 'top',
};

// ============================================================================
// ANALYTICS & TRACKING
// ============================================================================

export const TRACKING_LABELS = {
    EVENT_NAME: 'Event Search Filter Applied',
    LOCATION: 'Location',
    OPEN_REGISTRATION: 'Open Registration',
    NOW_PLAYING: 'Now Playing',
    UPCOMING: 'Upcoming',
    CLIENT: 'Web',
    EVENT_SERIES: 'Event Series',
    MORE: 'More',
};
